// Invitations (arc 101 slice 04) — ONE object for two asks that were the same
// missing thing:
//
//   1. "Invite these five people to the platform."  The platform is invite-only
//      (`users.approved_at`), and until now the only lever acted on a user who
//      had ALREADY signed up (`user approve`). There was no way to address
//      someone who had not.
//   2. "Add my teammate to the org."  `member invite` resolves a GitHub login to
//      a registered user and refuses `unknown_login` otherwise, sending no mail.
//
// Both are "grant this ADDRESS something, before an identity exists for it".
//
// EMAIL IS THE ADDRESS, THE VERIFIED IDENTITY IS THE IDENTITY. The invitation
// names an email because that is the only channel that works before signup; the
// ACCEPTANCE binds a GitHub (or SSO) identity the platform verified. Nothing
// here ever treats the email as proof of who someone is: it is where the bearer
// token was sent, and the token is what authorizes.
//
// STATUS IS DERIVED, NEVER STORED. `pending | accepted | revoked | expired` is a
// pure function of three timestamps plus the clock ({@link invitationStatus}).
// A stored status column would be a second answer that can disagree with the
// timestamps it was computed from, and expiry would need a cron to flip rows
// that a read can decide for free.
//
// Pure types — no I/O.

import { z } from 'zod';
import { type Role, RoleSchema } from '../entities/account';
import { GRANT_AMOUNT_MAX } from '../entities/grant';
import { type AccountId, AccountIdSchema, InvitationIdSchema } from '../value-objects/ids';

/**
 * What the invitation grants. The two kinds are NOT interchangeable and the
 * shape check below binds each to its own columns:
 *
 * - `platform` — lifts the closed-beta waitlist cap (`users.approved_at`). It
 *   names no account and no role: platform access is not membership anywhere.
 * - `org_member` — inserts a membership in ONE account at ONE role. It names
 *   both, and grants nothing at the platform level on its own.
 */
export const InvitationKind = {
  Platform: 'platform',
  OrgMember: 'org_member',
} as const;
export const InvitationKindSchema = z.enum(InvitationKind);
export type InvitationKind = z.infer<typeof InvitationKindSchema>;

/**
 * Where an invitation stands, DERIVED from `(acceptedAt, revokedAt, expiresAt)`
 * and the clock. Not a column — see the module header.
 */
export const InvitationStatus = {
  /** Live and unused: the only state {@link acceptableInvitation} admits. */
  Pending: 'pending',
  /** Settled by the invitee; `acceptedAt` + `acceptedUserId` are both set. */
  Accepted: 'accepted',
  /** Settled by the inviter. Terminal: a revoked invitation is never revived. */
  Revoked: 'revoked',
  /** Unsettled and past `expiresAt`. Terminal: re-inviting mints a fresh token. */
  Expired: 'expired',
} as const;
export const InvitationStatusSchema = z.enum(InvitationStatus);
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

/**
 * Default time-to-live. Fourteen days: long enough that an invitation sent on a
 * Friday survives a holiday week, short enough that a link forwarded out of an
 * inbox a year later is dead. The value is the WRITE's default, never a read-time
 * fallback — `expires_at` is NOT NULL, so no row can exist without one.
 */
export const INVITATION_TTL_DAYS = 14;
export const INVITATION_TTL_MS = INVITATION_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * The ONE normalization seam for an invited address. Lowercased and trimmed, so
 * "Ada@Example.COM " and "ada@example.com" are one invitation and not two — the
 * same case-insensitivity GitHub logins already get in `findUserByLogin` (#916).
 *
 * Everything downstream assumes the stored value is already through here: the
 * live-invitation unique index compares the column directly, and the DB carries
 * `invitations_email_normalized_ck` so a writer that skips this cannot store a
 * value the lookup would then miss.
 */
export function normalizeInvitationEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** An invited address. Bounded so a free-text column cannot be a payload. */
export const InvitationEmailSchema = z
  .string()
  .trim()
  .min(3)
  .max(320)
  .email()
  .transform(normalizeInvitationEmail);

/**
 * What accepting a `platform` invitation GRANTS — booked on the invitation at
 * mint (a person who has not registered yet has no user row to grant against)
 * and issued on accept. Stored as `invitations.capacity` (jsonb), platform kind
 * only: an `org_member` invitation grants a membership, not capacity, and both
 * the parse below and `invitations_capacity_ck` refuse the pairing.
 *
 * Structurally identical to `@developerz/mcp`'s `UserProvisionBundle` — the
 * domain declares the shape, mcp's type is the same fields; neither imports the
 * other (domain imports nothing internal). `aiDevs` is bounded by the DB grant
 * ceiling (`grants_amount_ck`, `GRANT_AMOUNT_MAX`) so a booking can never be
 * one the accept cannot issue.
 *
 * `expiresInDays: null` is an ONGOING grant (plan 102 slice 05): the accept
 * issues it with `expires_at` null. Widened additively — every row written
 * before carries a number and still parses.
 */
export const InvitationCapacitySchema = z.object({
  aiDevs: z.number().int().positive().max(GRANT_AMOUNT_MAX),
  expiresInDays: z.number().int().positive().nullable(),
  /**
   * The issued grant's `extends_to_owned_orgs` (`entities/grant.ts`). Absent reads as off: every
   * row booked before this field carries none and still means what it meant.
   */
  extendsToOwnedOrgs: z.boolean().optional(),
});
export type InvitationCapacity = z.infer<typeof InvitationCapacitySchema>;

/**
 * One invitation row. `token_hash` is deliberately ABSENT from this type: the
 * hash is a credential-derived secret that only `@developerz/db` handles, and
 * the plaintext token exists once, in the mint's return value. A projection that
 * cannot carry either cannot leak either.
 */
export const InvitationSchema = z
  .object({
    id: InvitationIdSchema,
    kind: InvitationKindSchema,
    /** null for `platform`; the target org for `org_member`. */
    accountId: AccountIdSchema.nullable(),
    /** null for `platform`; the membership role to grant for `org_member`. */
    role: RoleSchema.nullable(),
    email: InvitationEmailSchema,
    /** Who is accountable. Null once that human is offboarded (`set null`). */
    invitedByUserId: z.string().uuid().nullable(),
    expiresAt: z.date(),
    acceptedAt: z.date().nullable(),
    acceptedUserId: z.string().uuid().nullable(),
    revokedAt: z.date().nullable(),
    createdAt: z.date(),
    /** The booked grant for a `platform` row; null for `org_member` and for a platform row that grants nothing extra. */
    capacity: InvitationCapacitySchema.nullable(),
  })
  .superRefine((r, ctx) => {
    const wanted = r.kind === InvitationKind.OrgMember;
    if (wanted && r.capacity !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capacity'],
        message: 'an org_member invitation grants a membership, never capacity',
      });
    }
    if ((r.accountId !== null) !== wanted) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['accountId'],
        message: `an ${r.kind} invitation ${wanted ? 'must name' : 'names no'} account`,
      });
    }
    if ((r.role !== null) !== wanted) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['role'],
        message: `an ${r.kind} invitation ${wanted ? 'must name' : 'names no'} role`,
      });
    }
    // Acceptance is ONE act: the stamp and the identity it bound are written
    // together or not at all. A date with no user is an access grant with nobody
    // behind it; a user with no date is a grant that no read can order or expire.
    if ((r.acceptedAt === null) !== (r.acceptedUserId === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['acceptedUserId'],
        message: 'acceptedAt and acceptedUserId are written together or not at all',
      });
    }
  });
export type Invitation = z.infer<typeof InvitationSchema>;

/**
 * The derived status. Settlement beats the clock in both directions: an
 * invitation ACCEPTED before it lapsed stays `accepted` forever (the grant
 * happened), and one REVOKED before it lapsed stays `revoked` (the inviter's
 * act is what an auditor asks about, not the calendar).
 *
 * Acceptance is checked before revocation because the two are mutually exclusive
 * by construction — `acceptInvitation` settles under a row lock and refuses a
 * revoked row — so the order only decides what a hand-written row would report,
 * and reporting a granted membership as revoked would be the wrong lie.
 */
export function invitationStatus(
  invitation: Pick<Invitation, 'acceptedAt' | 'revokedAt' | 'expiresAt'>,
  now: Date = new Date(),
): InvitationStatus {
  if (invitation.acceptedAt !== null) return InvitationStatus.Accepted;
  if (invitation.revokedAt !== null) return InvitationStatus.Revoked;
  if (invitation.expiresAt.getTime() <= now.getTime()) return InvitationStatus.Expired;
  return InvitationStatus.Pending;
}

/** True iff the invitation may still be redeemed. The read-time expiry predicate. */
export function acceptableInvitation(
  invitation: Pick<Invitation, 'acceptedAt' | 'revokedAt' | 'expiresAt'>,
  now: Date = new Date(),
): boolean {
  return invitationStatus(invitation, now) === InvitationStatus.Pending;
}

/**
 * Why an acceptance did not grant anything. NAMED rather than folded into one
 * `null`, because each is fixed by a different act and three of them are
 * indistinguishable from the outside otherwise:
 *
 * | Verdict | What the holder should do |
 * |---|---|
 * | `expired` | ask the inviter to re-invite (a fresh token, same row) |
 * | `revoked` | the inviter withdrew it; nothing to retry |
 * | `already_accepted` | nothing, if `bySelf`; otherwise someone else redeemed it |
 * | `not_found` | the token is wrong or was rotated by a re-invite |
 * | `already_a_member` | already in; the invitation settles anyway |
 *
 * `already_a_member` is a SUCCESS-shaped refusal on purpose: the requested end
 * state already holds. It is separate from `accepted` so an audit row can say
 * whether this call granted anything.
 *
 * Two arms are deliberately ABSENT rather than overlooked. There is no
 * `wrong_account`: an accept is addressed by the TOKEN, which names its own
 * target, so there is no second account for the caller to be wrong about (the
 * arm exists on {@link InvitationRevokeVerdict}, where the caller does supply an
 * account fence). And there is no `account_gone`: `invitations.account_id`
 * CASCADEs, so an offboarded org takes its invitations with it and the token
 * resolves to `not_found` — an arm for it would be an unreachable branch, which
 * is an untested branch.
 */
export type InvitationAcceptVerdict =
  | {
      readonly kind: 'accepted';
      readonly invitationId: string;
      readonly invitationKind: InvitationKind;
      readonly accountId: AccountId | null;
      readonly role: Role | null;
    }
  | { readonly kind: 'already_accepted'; readonly acceptedAt: Date; readonly bySelf: boolean }
  | { readonly kind: 'expired'; readonly expiresAt: Date }
  | { readonly kind: 'revoked'; readonly revokedAt: Date }
  | { readonly kind: 'not_found' }
  | {
      readonly kind: 'already_a_member';
      readonly accountId: AccountId;
      readonly role: Role;
    };

/**
 * Why a revoke changed nothing. `wrong_account` is separate from `not_found`
 * FOR THE INVITER and collapsed for everyone else: an org owner who mistypes an
 * id deserves to know the invitation exists elsewhere, and an anonymous prober
 * must not learn that it does. The transports decide which audience they are
 * answering; this vocabulary is what makes the distinction available at all.
 */
export type InvitationRevokeVerdict =
  | { readonly kind: 'revoked'; readonly invitationId: string; readonly revokedAt: Date }
  | { readonly kind: 'already_revoked'; readonly revokedAt: Date }
  | { readonly kind: 'already_accepted'; readonly acceptedAt: Date }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'wrong_account' };

/**
 * The listing projection — everything about an invitation EXCEPT anything the
 * bearer token can be derived from. `status` is computed at read against the
 * caller's clock, so a listing never shows a lapsed invitation as pending.
 *
 * There is no `token` field and no `accept_url`, and that is the point: the
 * plaintext is returned by the MINT and nowhere else, exactly like `token mint`.
 * A list read that echoed it would turn every `read` scope on this surface into
 * a credential-harvesting read.
 */
export type InvitationView = {
  id: string;
  kind: InvitationKind;
  account_id: string | null;
  role: Role | null;
  email: string;
  status: InvitationStatus;
  invited_by_user_id: string | null;
  expires_at_iso: string;
  created_at_iso: string;
  accepted_at_iso: string | null;
  revoked_at_iso: string | null;
};

/** Project one row for a listing surface. Never carries the token or its hash. */
export function invitationView(invitation: Invitation, now: Date = new Date()): InvitationView {
  return {
    id: invitation.id,
    kind: invitation.kind,
    account_id: invitation.accountId,
    role: invitation.role,
    email: invitation.email,
    status: invitationStatus(invitation, now),
    invited_by_user_id: invitation.invitedByUserId,
    expires_at_iso: invitation.expiresAt.toISOString(),
    created_at_iso: invitation.createdAt.toISOString(),
    accepted_at_iso: invitation.acceptedAt?.toISOString() ?? null,
    revoked_at_iso: invitation.revokedAt?.toISOString() ?? null,
  };
}

/**
 * The accept link a mint hands back exactly once. Built here so the dashboard
 * route, the email body and the MCP response cannot drift into three shapes.
 * The token rides the FRAGMENT-free query of a path the SPA owns; it is a
 * one-time bearer credential, so the page must POST it and never render it.
 */
export function invitationAcceptUrl(dashboardUrl: string, token: string): string {
  const base = dashboardUrl.replace(/\/+$/, '');
  return `${base}/invite?token=${encodeURIComponent(token)}`;
}
